import React, { lazy, Suspense, PureComponent } from 'react';
import Radium from 'radium';
import { Route, Switch, Redirect } from 'react-router-dom';
import { bool, shape, string, number } from 'prop-types';
import { UTLoading } from '@widergy/energy-ui';

import { ROUTES } from 'constants/routes';
import { accountPropTypes } from 'redux/accountHandlers';
import { appConfig } from 'config/customization';
import Configuration from 'app/screens/Configuration';
import ChangeUserInfo from 'app/screens/Configuration/screens/ChangeUserInfo';
import ExternalDocumentPayments from 'app/screens/PublicDashboard/screens/PaymentDocuments';
import AppDownloader from 'app/screens/AppDownloader';
import PendingFineAmountQuery from 'app/components/PendingFineAmountQuery';
import PendingWarrantyDepositQuery from 'app/components/PendingWarrantyDepositQuery';
import Sidebar from 'app/components/Sidebar';
import Topbar from 'app/components/Topbar';
import Survey from 'app/components/Survey';

import styles from './styles';
import AccountStatus from './screens/AccountStatus';
import Accounts from './screens/Accounts';
import AccountDetailMobileContainer from './screens/Accounts/components/AccountDetailMobile';
import EnergyAdvisor from './screens/EnergyAdvisor';
import ClientSummary from './screens/ClientSummary';
import Claims from './screens/Claims';
import Complaints from './screens/Complaints';
import NewComplaint from './screens/Complaints/screens/NewComplaint';
import Procedures from './screens/Procedures';
import SocialRate from './screens/SocialRate';
import Bills from './screens/Bills';
import BillDetails from './screens/Bills/screens/BillDetails';
import Payments from './screens/Payments';
import ExternalPayments from './screens/ExternalPayments';
import Tips from './screens/EnergyAdvisor/components/Tips';
import ChangeDueDate from './screens/ChangeDueDate';
import AccountHolderChange from './screens/AccountHolderChange';
import TerminationRequest from './screens/TerminationRequest';
import PaymentRequest from './screens/PaymentRequest';
import RegisterAccount from './screens/RegisterAccount';
import Turns from './screens/Turns';

const Points = lazy(() => import('./screens/Points'));
const PointsHistory = lazy(() => import('./screens/Points/screens/PointsHistory'));
const Vouchers = lazy(() => import('./screens/Vouchers'));

class Dashboard extends PureComponent {
  render() {
    const { loadingAuth, location, displayAdvice, alertHeight, currentAccount } = this.props;

    const stylesDashboard = { height: `calc(100vh - ${alertHeight}px)` };
    return (
      <div style={[styles.base, stylesDashboard]}>
        <div style={styles.baseContent}>
          {(appConfig.survey.payment || appConfig.survey.procedure) && <Survey />}
          <Topbar
            useClientSummary={currentAccount && appConfig.useClientSummary}
            clientSummary={location.pathname === ROUTES.CLIENT_SUMMARY}
          />
          <Sidebar location={location} />
          {displayAdvice && <AppDownloader />}
          <div style={styles.content}>
            <UTLoading loading={loadingAuth}>
              <Suspense fallback={<UTLoading />}>
                <Switch>
                  {appConfig.turns.active && <Route exact path={ROUTES.MY_TURNS} component={Turns} />}
                  {appConfig.turns.active && <Route exact path={ROUTES.NEW_TURN} component={Turns} />}
                  <Route exact path={ROUTES.ACCOUNT_STATUS} component={AccountStatus} />
                  <Route exact path={ROUTES.PAYMENT_GATEWAY} component={Payments} />
                  <Route exact path={ROUTES.EXTERNAL_PAYMENTS} component={ExternalPayments} />
                  <Route
                    exact
                    path={ROUTES.EXTERNAL_DOCUMENT_PAYMENTS}
                    component={ExternalDocumentPayments}
                  />
                  {appConfig.useClientSummary && (
                    <Route exact path={ROUTES.CLIENT_SUMMARY} component={ClientSummary} />
                  )}
                  {appConfig.dashboard.pendingWarrantyDepositQuery && (
                    <Route
                      exact
                      path={ROUTES.PENDING_WARRANTY_DEPOSIT_QUERY}
                      component={PendingWarrantyDepositQuery}
                    />
                  )}
                  {appConfig.dashboard.pendingFineAmountQuery && (
                    <Route exact path={ROUTES.PENDING_FINE_AMOUNT_QUERY} component={PendingFineAmountQuery} />
                  )}
                  {appConfig.claims.useClaims && <Route exact path={ROUTES.CLAIMS} component={Claims} />}
                  {appConfig.complaints.useComplaints && (
                    <Route exact path={ROUTES.COMPLAINTS} component={Complaints} />
                  )}
                  {appConfig.complaints.useComplaints && (
                    <Route exact path={ROUTES.NEW_COMPLAINT} component={NewComplaint} />
                  )}
                  {appConfig.procedures.useProcedures && (
                    <Route exact path={ROUTES.PROCEDURES} component={Procedures} />
                  )}
                  {appConfig.socialRate.useSocialRate && (
                    <Route exact path={ROUTES.SOCIAL_RATE} component={SocialRate} />
                  )}
                  {appConfig.includesHolderChangeProcedure && (
                    <Route exact path={ROUTES.NEW_ACCOUNT_HOLDER_CHANGE} component={AccountHolderChange} />
                  )}
                  {appConfig.includesRegisterAccount && (
                    <Route exact path={ROUTES.REGISTER_ACCOUNT} component={RegisterAccount} />
                  )}
                  {appConfig.includesTerminationService && (
                    <Route exact path={ROUTES.NEW_TERMINATION_REQUEST} component={TerminationRequest} />
                  )}
                  {appConfig.usePoints && <Route exact path={ROUTES.POINTS} component={Points} />}
                  {appConfig.usePoints && (
                    <Route exact path={ROUTES.POINTS_HISTORY} component={PointsHistory} />
                  )}
                  <Route path={ROUTES.CONFIGURATION} component={Configuration} />
                  <Route exact path={ROUTES.ACCOUNTS} component={Accounts} />
                  {appConfig.usePoints && <Route exact path={ROUTES.VOUCHERS} component={Vouchers} />}
                  <Route exact path={ROUTES.BILLS} component={Bills} />
                  <Route exact path={ROUTES.BILLS_DETAIL} component={BillDetails} />
                  {appConfig.payments.active && <Route exact path={ROUTES.PAYMENTS} component={Payments} />}
                  <Route exact path={ROUTES.ENERGY_ADVISOR} component={EnergyAdvisor} />
                  <Route exact path={ROUTES.ENERGY_ADVISOR_TIPS} component={Tips} />
                  <Route exact path={ROUTES.CHANGE_DUE_DATE} component={ChangeDueDate} />
                  <Route exact path={ROUTES.CHANGE_USER_INFO} component={ChangeUserInfo} />
                  <Route exact path={ROUTES.PAYMENT_REQUEST} component={PaymentRequest} />
                  <Route exact path={ROUTES.ACCOUNTS_DETAIL} component={AccountDetailMobileContainer} />
                  <Route render={() => <Redirect to={ROUTES.ACCOUNT_STATUS} />} />
                </Switch>
              </Suspense>
            </UTLoading>
          </div>
        </div>
      </div>
    );
  }
}

Dashboard.propTypes = {
  loadingAuth: bool,
  location: shape({
    pathname: string.isRequired
  }),
  displayAdvice: bool,
  alertHeight: number,
  currentAccount: accountPropTypes
};

export default Radium(Dashboard);
